import React, { useEffect, useState } from 'react';
import styles from './SettingsModal.module.css';
import IconButton from '../../../../components/admin/IconButton';
import { ICON_PATHS } from "../../../../constants/iconPaths";

function SettingsModal({ settings, onSettingsChange, onClose, isWideScreen }) {
  const [startTime, setStartTime] = useState('19:00');
  const [endTime, setEndTime] = useState('21:00');
  const [tempSettings, setTempSettings] = useState({ ...settings });

  // 참가자 수가 변경될 때 매치 타입과 키퍼 수 자동 조정
  useEffect(() => {
    const updates = {};
    
    if (tempSettings.totalPlayers < 12 && tempSettings.matchType === '6vs6') {
      updates.matchType = '5vs5';
    }

    const shouldHaveTwoKeepers = 
      (tempSettings.matchType === '6vs6' && tempSettings.totalPlayers >= 17) || 
      (tempSettings.matchType === '5vs5' && tempSettings.totalPlayers >= 13);
    
    if (shouldHaveTwoKeepers !== (tempSettings.keepersPerQuarter === 2)) {
      updates.keepersPerQuarter = shouldHaveTwoKeepers ? 2 : 1;
    }

    if (Object.keys(updates).length > 0) {
      setTempSettings(prev => ({ ...prev, ...updates }));
    }
  }, [tempSettings.totalPlayers, tempSettings.matchType]);

  const handleChange = (key, value) => {
    setTempSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (window.confirm('설정을 저장하시겠습니까? 현재 진행 중인 경기가 초기화됩니다.')) {
      // 계산된 모든 설정값을 포함하여 저장
      const quarterSettings = calculateQuarterSettings();
      const newSettings = {
        ...tempSettings,
        totalTeams,
        quarters: quarterSettings.quarters,
        quarterTime: quarterSettings.quarterTime,
        keepersPerQuarter: calculateKeepersPerQuarter()
      };
      
      onSettingsChange(newSettings);
      
      if (!isWideScreen) {
        onClose();
      }
    }
  };

  // 팀 수 계산
  const totalTeams = tempSettings.matchType === '6vs6' 
    ? (tempSettings.totalPlayers >= 17 ? 3 : 2)
    : (tempSettings.totalPlayers >= 13 ? 3 : 2);

  // 쿼터 수와 시간 계산
  const calculateQuarterSettings = () => {
    if ((tempSettings.matchType === '6vs6' && tempSettings.totalPlayers >= 17) || 
        (tempSettings.matchType === '5vs5' && tempSettings.totalPlayers >= 13)) {
      return { quarters: 9, quarterTime: 12 };
    } else if (tempSettings.matchType === '6vs6') {
      if (tempSettings.totalPlayers >= 15) {
        return { quarters: 8, quarterTime: 15 };
      } else if (tempSettings.totalPlayers >= 13) {
        return { quarters: 7, quarterTime: 17 };
      }
    }
    return { quarters: 6, quarterTime: 20 };
  };

  const quarterSettings = calculateQuarterSettings();

  const modalClass = isWideScreen ? styles.sideContent : styles.modal;
  const overlayClass = isWideScreen ? '' : styles.modalOverlay;

  // 키퍼 수 계산
  const calculateKeepersPerQuarter = () => {
    return (tempSettings.matchType === '6vs6' && tempSettings.totalPlayers >= 17) || 
           (tempSettings.matchType === '5vs5' && tempSettings.totalPlayers >= 13) ? 2 : 1;
  };

  const calculateTotalTime = (start, end) => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    let totalMinutes = (endHour - startHour) * 60 + (endMin - startMin);
    if (totalMinutes < 0) {
      totalMinutes += 24 * 60; // 다음날로 넘어가는 경우
    }
    
    return totalMinutes;
  };

  const handleTimeChange = (type, value) => {
    if (type === 'start') {
      setStartTime(value);
      const totalTime = calculateTotalTime(value, endTime);
      setTempSettings(prev => ({ ...prev, totalTime }));
    } else {
      setEndTime(value);
      const totalTime = calculateTotalTime(startTime, value);
      setTempSettings(prev => ({ ...prev, totalTime }));
    }
  };

  // 팀당 인원수 계산 함수
  const calculatePlayersPerTeam = () => {
    const totalPlayers = tempSettings.totalPlayers;
    const teams = ['b', 'r', 'y'].slice(0, totalTeams);
    const basePlayersPerTeam = Math.floor(totalPlayers / teams.length);
    const remainingPlayers = totalPlayers % teams.length;

    return teams.reduce((acc, team, index) => {
      acc[team] = basePlayersPerTeam + (index < remainingPlayers ? 1 : 0);
      return acc;
    }, {});
  };

  const playersPerTeam = calculatePlayersPerTeam();

  // 오버레이 클릭 핸들러 추가
  const handleOverlayClick = (e) => {
    // 오버레이 영역을 클릭했을 때만 모달 닫기
    if (e.target.className === styles.modalOverlay) {
      onClose();
    }
  };

  const content = (
    <div className={modalClass}>
      {!isWideScreen && (
        <div className={styles.header}>
          <h2>매치 설정</h2>
          <IconButton
            icon={ICON_PATHS.close}
            onClick={onClose}
            className={styles.closeButton}
            style={{ fill: 'var(--gray-500)' }}
          />
        </div>
      )}
      
      <div className={styles.content}>
        <div className={styles.settingItem}>
          <label>시작 시간</label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => handleTimeChange('start', e.target.value)}
            className={styles.timeInput}
          />
        </div>

        <div className={styles.settingItem}>
          <label>종료 시간</label>
          <input
            type="time"
            value={endTime}
            onChange={(e) => handleTimeChange('end', e.target.value)}
            className={styles.timeInput}
          />
        </div>

        <div className={styles.settingItem}>
          <label>매치 타입</label>
          <select
            value={tempSettings.matchType}
            onChange={(e) => handleChange('matchType', e.target.value)}
            disabled={tempSettings.totalPlayers < 12}
          >
            <option value="5vs5">5vs5</option>
            <option value="6vs6">6vs6</option>
          </select>
          {tempSettings.totalPlayers < 12 && (
            <div className={styles.settingHint}>
              12명 미만은 5vs5만 가능합니다
            </div>
          )}
        </div>
        <div className={styles.settingItem}>
          <label>참가자 수</label>
          <select
            value={tempSettings.totalPlayers}
            onChange={(e) => handleChange('totalPlayers', parseInt(e.target.value))}
          >
            {Array.from({length: 9}, (_, i) => i + 10).map(num => (
              <option key={num} value={num}>{num}명</option>
            ))}
          </select>
        </div>

        <div className={styles.infoContainer}>
            <div className={styles.infoItem}>
            <label>전체 시간</label>
            <span className={styles.infoValue}>{tempSettings.totalTime}분</span>
            </div>
            <div className={styles.infoItem}>
            <label>팀 수</label>
            <span className={styles.infoValue}>{totalTeams}팀</span>
            </div>

            <div className={styles.infoItem}>
            <label>쿼터 수</label>
            <span className={styles.infoValue}>{quarterSettings.quarters}쿼터</span>
            </div>

            <div className={styles.infoItem}>
            <label>쿼터 시간</label>
            <span className={styles.infoValue}>{quarterSettings.quarterTime}분</span>
            </div>

            <div className={styles.infoItem}>
            <label>쿼터당 키퍼 수</label>
            <span className={styles.infoValue}>
                {calculateKeepersPerQuarter()}명
            </span>
            </div>

            <div className={styles.infoItem}>
              <label>팀당 인원</label>
              <div className={styles.teamPlayersInfo}>
                {Object.entries(playersPerTeam).map(([team, count]) => (
                  <div key={team} className={`${styles.teamPlayers} ${styles[team]}`}>
                    {team.toUpperCase()}: {count}명
                  </div>
                ))}
              </div>
            </div>
        </div>

        <div className={styles.buttonContainer}>
          <button 
            className={styles.saveButton}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );

  return isWideScreen ? content : (
    <div className={overlayClass} onClick={handleOverlayClick}>
      {content}
    </div>
  );
}

export default SettingsModal; 